import type { ElementRef, RefObject } from 'react';
import { useLayoutEffect } from 'react';

import type { FocusDirection, ScrollTargetPosition } from '@/newstore/slices/shared';

export default function useFocusMessageListElement({
  elementRef,
  isFocused,
  focusVersion,
  focusDirection,
  scrollTargetPosition,
}: {
  elementRef: RefObject<ElementRef<'div'>>;
  isFocused?: boolean;
  focusVersion?: number;
  focusDirection?: FocusDirection;
  scrollTargetPosition?: ScrollTargetPosition;
}) {
  useLayoutEffect(() => {
    if (isFocused && elementRef.current) {
      const messagesContainer = elementRef.current.closest<HTMLDivElement>('.MessageList');
      if (!messagesContainer) {
        return;
      }

      const exec = (behavior: ScrollBehavior) => {
        if (!elementRef.current) {
          return;
        }

        const block = scrollTargetPosition === 'centerOrTop' ? 'center' : (scrollTargetPosition ?? 'center');

        elementRef.current.scrollIntoView({
          block,
          behavior,
        });
      };

      exec('smooth');
      const retryTimeout = scrollTargetPosition === 'start' ? window.setTimeout(() => exec('auto'), 450) : undefined;

      return () => {
        if (retryTimeout) {
          clearTimeout(retryTimeout);
        }
      };
    }
  }, [elementRef, isFocused, focusVersion, focusDirection, scrollTargetPosition]);
}
